import React from 'react';
import { Lock, AlertTriangle, Ban, CheckCircle2, Clock, XCircle } from 'lucide-react';
import type { OportunidadeStatus, Prioridade } from '../api';

const STATUS_STYLES: Record<OportunidadeStatus, { className: string; label: string; Icon: React.ComponentType<{ className?: string }> }> = {
  aberta: { className: 'bg-blue-100 text-blue-800', label: 'Aberta', Icon: Clock },
  ganha: { className: 'bg-green-100 text-green-800', label: 'Ganha', Icon: CheckCircle2 },
  perdida: { className: 'bg-red-100 text-red-800', label: 'Perdida', Icon: XCircle },
  suspensa: { className: 'bg-yellow-100 text-yellow-800', label: 'Suspensa', Icon: Ban },
  cancelada: { className: 'bg-gray-200 text-gray-800', label: 'Cancelada', Icon: Ban },
};

const PRIORIDADE_STYLES: Record<Prioridade, { className: string; label: string }> = {
  baixa: { className: 'bg-gray-100 text-gray-700', label: 'Baixa' },
  media: { className: 'bg-blue-100 text-blue-800', label: 'Média' },
  alta: { className: 'bg-orange-100 text-orange-800', label: 'Alta' },
  critica: { className: 'bg-red-100 text-red-800', label: 'Crítica' },
};

export const OportunidadeStatusBadge: React.FC<{ status: OportunidadeStatus }> = ({ status }) => {
  const s = STATUS_STYLES[status];
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.className}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {s.label}
    </span>
  );
};

export const OportunidadePrioridadeBadge: React.FC<{ prioridade: Prioridade }> = ({ prioridade }) => {
  const p = PRIORIDADE_STYLES[prioridade];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${p.className}`}>
      {p.label}
    </span>
  );
};

/**
 * Badge de confidencialidade (§Confidencialidade + task 16.3). Quem
 * enxerga o registro pode ver o badge — a confidencialidade OWASP
 * (404) já é aplicada no backend, então a listagem só entrega quem
 * tem permissão. O badge sinaliza que a informação exige cautela.
 */
export const ConfidencialBadge: React.FC = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
    title="Oportunidade confidencial — acesso restrito ao responsável, participantes e perfis de gestão."
  >
    <Lock className="h-3 w-3" aria-hidden="true" />
    Confidencial
  </span>
);

/**
 * Badge de estagnação — oportunidade sem movimentação por mais do
 * que o prazo configurado por prioridade (spec §Oportunidade Parada).
 */
export const StalledBadge: React.FC = () => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
    title="Sem movimentação há mais tempo do que o esperado para a prioridade."
  >
    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
    Parada
  </span>
);
